import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Spinner } from '@/components/common/Spinner';

export default function AuthCallbackPage() {
  const { handleOidcCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // tfx-auth-service delivers tokens via URL fragment:
    // /auth/callback#access_token=xxx&refresh_token=yyy
    // Errors are also delivered via fragment:
    // /auth/callback#error=access_denied&error_description=...
    const hash = window.location.hash.substring(1); // strip leading '#'
    const params = new URLSearchParams(hash);

    // Check for auth errors in fragment first (e.g. domain not allowed)
    const fragmentError = params.get('error');
    if (fragmentError) {
      const description = params.get('error_description') || fragmentError;
      setError(description);
      return;
    }

    const token = params.get('access_token');

    // Recover the return-to path saved before the OIDC redirect.
    const returnTo = sessionStorage.getItem('tda_return_to') || undefined;
    sessionStorage.removeItem('tda_return_to');

    if (!token) {
      // Also check query params as fallback
      const queryParams = new URLSearchParams(window.location.search);
      const queryToken = queryParams.get('token') || queryParams.get('access_token');
      const errorMsg = queryParams.get('error');
      const errorDesc = queryParams.get('error_description');

      if (queryToken) {
        handleOidcCallback(queryToken, returnTo).catch((err) => {
          setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
        });
        return;
      }

      setError(errorDesc || errorMsg || 'No authentication token received. Please try again.');
      return;
    }

    handleOidcCallback(token, returnTo).catch((err) => {
      setError(err instanceof Error ? err.message : 'Authentication failed. Please try again.');
    });
  }, [handleOidcCallback]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-md-cream">
        <Card className="w-full max-w-md p-8 text-center">
          <h2 className="text-xl font-bold text-md-graphite mb-4">
            Sign-in Failed
          </h2>
          <p className="text-md-error mb-6">{error}</p>
          <a
            href="/login"
            className="text-md-sky-strong underline hover:text-md-sky"
          >
            Return to login
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-md-cream">
      <Card className="w-full max-w-md p-8 text-center">
        <Spinner size="lg" />
        <p className="text-md-ink mt-4">Completing sign-in...</p>
      </Card>
    </div>
  );
}
